import type { FC } from "react";
import Link from "next/link";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

export type BreadcrumbItem = {
  label: string;
  href?: string;
};

/** `dark` is the original directory chrome, unchanged. `light` is the profile page's dark-blue
 *  hero band (947:34050), where the default text and separator colours would be invisible. */
const breadcrumbTrailVariants = cva("text-14 font-medium lg:text-16", {
  variants: {
    tone: {
      dark: "text-neutral-black opacity-50",
      light: "text-neutral-white opacity-50",
    },
  },
  defaultVariants: {
    tone: "dark",
  },
});

const breadcrumbLastVariants = cva("text-14 font-medium lg:text-16", {
  variants: {
    tone: {
      dark: "text-brand-dark-blue",
      light: "text-neutral-white",
    },
  },
  defaultVariants: {
    tone: "dark",
  },
});

const SEPARATOR_TONE = {
  dark: "bg-neutral-black/50",
  light: "bg-neutral-white/50",
} as const satisfies Record<NonNullable<VariantProps<typeof breadcrumbTrailVariants>["tone"]>, string>;

/** Shrink order when the trail outgrows its container: the middle crumbs collapse to an ellipsis
 *  first, then the first crumb, and the current page keeps as much of its label as it can. */
const shrinkPriority = (isFirst: boolean, isLast: boolean) => {
  if (isLast) return "shrink";
  return isFirst ? "shrink-[80]" : "shrink-[9999]";
};

const LABEL_CLASSES = "block min-w-12 truncate";

type BreadcrumbNavigationProps = VariantProps<typeof breadcrumbTrailVariants> & {
  items: BreadcrumbItem[];
};

export const BreadcrumbNavigation: FC<BreadcrumbNavigationProps> = ({ items, tone }) => {
  const resolvedTone = tone ?? "dark";

  return (
    <nav className="flex min-w-0 max-w-full items-center gap-12">
      {items.map((item, index) => {
        const isLast = index === items.length - 1;
        return (
          <div
            key={item.label}
            title={item.label}
            className={cn("flex min-w-0 items-center gap-12", shrinkPriority(index === 0, isLast))}
          >
            {index > 0 && <span className={cn("h-14 w-px shrink-0", SEPARATOR_TONE[resolvedTone])} />}
            {item.href && !isLast ? (
              <Link
                href={item.href}
                className={cn(breadcrumbTrailVariants({ tone: resolvedTone }), LABEL_CLASSES, "hover:opacity-100")}
              >
                {item.label}
              </Link>
            ) : (
              <span
                className={cn(
                  isLast
                    ? breadcrumbLastVariants({ tone: resolvedTone })
                    : breadcrumbTrailVariants({ tone: resolvedTone }),
                  LABEL_CLASSES,
                )}
              >
                {item.label}
              </span>
            )}
          </div>
        );
      })}
    </nav>
  );
};
